import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ScrollOverflow {
  /** Attach to the scrolling element. */
  scrollRef: RefObject<HTMLDivElement>;
  /** Attach to the element wrapping the scrolled content, so growth is observed too. */
  contentRef: RefObject<HTMLDivElement>;
  /** Content is clipped above the current viewport. */
  overflowTop: boolean;
  /** Content is clipped below the current viewport. */
  overflowBottom: boolean;
  /** Content is clipped left of the current viewport — for a sideways strip (tabs). */
  overflowLeft: boolean;
  /** Content is clipped right of the current viewport. */
  overflowRight: boolean;
}

/**
 * Reports whether a scroll container has content hidden above or below (or left / right, for a
 * sideways strip such as tabs), so callers can draw an edge indicator or scroll arrows. Both the container and its content are observed: the container changes with the
 * viewport, while the content changes whenever a menu section expands or collapses — and with
 * several sections open at once, that is exactly when rows start disappearing off the edges.
 */
export const useScrollOverflow = (): ScrollOverflow => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflowTop, setOverflowTop] = useState(false);
  const [overflowBottom, setOverflowBottom] = useState(false);
  const [overflowLeft, setOverflowLeft] = useState(false);
  const [overflowRight, setOverflowRight] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // A sub-pixel tolerance: fractional layout sizes otherwise leave the indicator permanently on.
    const EPSILON = 1;
    setOverflowTop(el.scrollTop > EPSILON);
    setOverflowBottom(el.scrollTop + el.clientHeight < el.scrollHeight - EPSILON);
    setOverflowLeft(el.scrollLeft > EPSILON);
    setOverflowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - EPSILON);
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', measure, { passive: true });
    // The accordion animates its height, so the size settles over ~200ms rather than in one step;
    // ResizeObserver fires throughout, which keeps the indicator in step with the animation.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);

  return { scrollRef, contentRef, overflowTop, overflowBottom, overflowLeft, overflowRight };
};

export default useScrollOverflow;
